import { useEffect, useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchSession, sessionQueryKey, hasModule } from "@/lib/auth";
import { fmtDateTime } from "@/lib/dates";
import { applyTheme, getStoredPreference, resolveTheme } from "@/theme";
import { getBulkCountSheet, worklistKeys } from "@/features/worklists/worklists.api";
import "@/features/worklists/bulkCountSheet.css";

// The count list of bulk locations (worklist "tellijst-bulk"), on a page of its own like the count sheet as a
// picture: it is paper to print, so no world bar. Every bulk location of the warehouse with its pallets according
// to the system, empty ones too, and a box per location for the number counted (Camiel, 05-11-2026). The worklists
// screen sends you here right after the request; the pallets come from the WMS through DataBridge a moment later,
// so the page asks until they are in.
//
// The paper is in English unless Dutch is chosen (paper=nl), as on the picture: FrieslandCampina audits the counts
// in English. The screen around it keeps the user's language.

const POLL_MS = 2000;
const SLOW_AFTER_MS = 60_000;
const GIVE_UP_AFTER_MS = 10 * 60_000;
type PaperLang = "nl" | "en";
const stockTime = (d: Date, lang: PaperLang) =>
  new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "nl-NL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/Amsterdam",
  }).format(d);

export const Route = createFileRoute("/bulk-count-sheet")({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });
    return { session };
  },
  validateSearch: (search: Record<string, unknown>): { request: string; paper?: PaperLang } => ({
    request: typeof search["request"] === "string" ? search["request"] : "",
    paper: search["paper"] === "nl" ? "nl" : undefined,
  }),
  component: BulkCountSheetPage,
});

function BulkCountSheetPage() {
  const { t, i18n } = useTranslation();
  const { request, paper = "en" } = Route.useSearch();
  const tPaper = i18n.getFixedT(paper);
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const allowed = hasModule(session, "worklists");
  const [now, setNow] = useState(() => Date.now());

  // Paper has one look: the light theme while the list is open, the chosen one after.
  useEffect(() => {
    applyTheme("light");
    return () => applyTheme(resolveTheme(getStoredPreference()));
  }, []);

  const query = useQuery({
    queryKey: worklistKeys.bulkCountSheet(request),
    queryFn: () => getBulkCountSheet(request),
    enabled: allowed && request !== "",
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data || data.status !== "requested") return false;
      return Date.now() - new Date(data.requestedAt).getTime() < GIVE_UP_AFTER_MS ? POLL_MS : false;
    },
  });

  const data = query.data;
  const waiting = data?.status === "requested";
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => setNow(Date.now()), POLL_MS);
    return () => window.clearInterval(timer);
  }, [waiting]);
  const slow = waiting && now - new Date(data.requestedAt).getTime() > SLOW_AFTER_MS;

  const places = data?.places ?? null;
  const printable = places !== null && places.length > 0;

  return (
    <div className="bc-page" data-screen-label="Back-office Bulk count list">
      <div className="bc-bar">
        <div className="bc-bar__row">
          <Link to="/worklists" className="wl-btn wl-btn--ghost">
            {t("countSheet.back")}
          </Link>
          <p className="t-body-sm t-muted bc-bar__status" role="status">
            {data && t("countSheet.requested", { name: data.requestedByName, when: fmtDateTime(new Date(data.requestedAt)) })}
            {printable && ` · ${t("bulkCountSheet.places", { count: places.length })}`}
          </p>
          <label className="t-body-sm bc-bar__paper">
            {t("countSheet.paper")}
            <select
              className="wl-select"
              value={paper}
              onChange={(e) =>
                void navigate({
                  search: (prev) => ({ ...prev, paper: e.target.value === "nl" ? "nl" : undefined }),
                  replace: true,
                })
              }
            >
              <option value="en">{t("countSheet.paperEn")}</option>
              <option value="nl">{t("countSheet.paperNl")}</option>
            </select>
          </label>
          <button type="button" className="wl-btn wl-btn--primary" disabled={!printable} onClick={() => window.print()}>
            {t("countSheet.print")}
          </button>
        </div>

        {!allowed && <div className="wl-alert wl-alert--danger">{t("countSheet.noAccess")}</div>}
        {allowed && request !== "" && query.isPending && <p className="t-body-sm t-muted">{t("common.loading")}</p>}
        {allowed && (request === "" || query.isError) && (
          <div className="wl-alert wl-alert--danger">
            {query.error instanceof Error ? query.error.message : t("countSheet.notFound")}
          </div>
        )}
        {waiting && (
          <div className={`wl-alert ${slow ? "wl-alert--warning" : "wl-alert--info"}`} role="status">
            {slow ? t("bulkCountSheet.slow") : t("countSheet.waiting")}
          </div>
        )}
        {data?.status === "failed" && (
          <div className="wl-alert wl-alert--danger" role="alert">
            <div>
              <strong>{t("countSheet.failed")}</strong>
              {data.message && <p className="t-body-sm">{data.message}</p>}
            </div>
          </div>
        )}
        {places !== null && places.length === 0 && data && (
          <div className="wl-alert wl-alert--info">{t("bulkCountSheet.none", { warehouse: data.warehouse.name })}</div>
        )}
      </div>

      {data && printable && (
        <div className="bc-scroll">
          <article className="bc-paper">
            <header className="bc-top">
              <div>
                <h1 className="bc-title">{tPaper("bulkCountSheet.title")}</h1>
                <p className="bc-sub">
                  {tPaper("bulkCountSheet.subtitle", { warehouse: data.warehouse.name, count: places.length })}
                </p>
              </div>
              <div className="bc-total">
                {tPaper("countSheet.systemTotal")}
                <strong>{tPaper("countSheet.pallets", { count: data.totalPallets ?? 0 })}</strong>
                {data.receivedAt && tPaper("countSheet.asOf", { when: stockTime(new Date(data.receivedAt), paper) })}
              </div>
            </header>
            <p className="bc-instruction">{tPaper("bulkCountSheet.instruction")}</p>
            <table className="bc-list">
              <thead>
                <tr>
                  <th className="bc-name">{tPaper("bulkCountSheet.location")}</th>
                  <th className="bc-num">{tPaper("countSheet.system")}</th>
                  <th className="bc-counted">{tPaper("countSheet.counted")}</th>
                  <th>{tPaper("bulkCountSheet.remark")}</th>
                </tr>
              </thead>
              <tbody>
                {places.map((p) => (
                  <tr key={p.name}>
                    <td className="bc-name">{p.name}</td>
                    <td className={p.pallets === 0 ? "bc-num bc-empty" : "bc-num"}>{p.pallets}</td>
                    <td className="bc-counted">
                      <span className="bc-box" />
                    </td>
                    <td className="bc-remark">{p.blocked ? tPaper("countSheet.legend.blocked") : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <footer className="bc-foot">
              <div className="bc-foot-total">
                {tPaper("countSheet.totalSystem")}
                <strong>{data.totalPallets ?? 0}</strong>
              </div>
              <div className="bc-field">{tPaper("countSheet.totalCounted")}</div>
              <div className="bc-field">{tPaper("countSheet.totalDeviating")}</div>
              <div className="bc-field">{tPaper("countSheet.name")}</div>
              <div className="bc-field">{tPaper("countSheet.date")}</div>
            </footer>
          </article>
        </div>
      )}
    </div>
  );
}
